import Image from "next/image";
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import Link from "next/link";

interface WhyChooseBaderData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  closing_text?: {
    text?: string;
  };
  image?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
  button?: {
    label?: string;
    href?: string;
  };
  list?: {
    site_content_id: {
      text: string;
      title: string;
    };
  }[];
}

export const fields = [
  "heading.text",
  "heading.title",

  "text.text",

  "closing_text.text",

  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",

  "button.label",
  "button.href",

  "list.site_content_id.text",
  "list.site_content_id.title",
] as const;

interface WhyChooseBaderProps {
  data?: WhyChooseBaderData;
}

export default function WhyChooseBader({ data }: WhyChooseBaderProps) {
  return (
    <section className="relative w-full overflow-hidden bg-[#dac79b]">
      <div className="mx-auto">
        <div className="flex flex-col lg:flex-row">
          <div className="relative lg:w-3/5 px-7 lg:px-32 pt-80 lg:pt-32 pb-8 lg:pb-32">
            <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mb-8 pt-8 lg:pt-0">
              {data?.heading?.title}
            </h2>
            <div
              className="text-[1rem] font-normal leading-normal lg:text-xl text-primary lg:leading-relaxed mb-8 lg:mb-16 [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal lg:[&>*]:text-xl lg:[&>*]:leading-relaxed [&_a]:underline [&_a]:transition-all [&_a]:duration-200 [&_a:hover]:no-underline"
              dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
            />
            <ul className="space-y-6 xl:space-y-0 xl:grid xl:grid-cols-3 xl:gap-8">
              {data?.list?.map((item, index) => (
                <li key={index} className="flex items-start gap-4">
                  <div className="w-2.5 h-2.5 mt-3 bg-primary rounded-tr-[20px] rounded-br-[20px]" />
                  <div
                    className="flex-1 text-[1rem] font-normal leading-normal lg:text-xl text-primary lg:leading-relaxed
                      [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal
                      lg:[&>*]:text-xl lg:[&>*]:leading-relaxed
                      [&_a]:underline [&_a]:transition-all [&_a]:duration-200 [&_a:hover]:no-underline
                      [&_strong]:font-bold"
                    dangerouslySetInnerHTML={sanitizeHTML(
                      item.site_content_id.text || ""
                    )}
                  />
                </li>
              ))}
            </ul>
            {data?.closing_text?.text && (
              <div
                className="text-[1rem] font-normal leading-normal lg:text-xl text-primary lg:leading-relaxed mt-8 [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal lg:[&>*]:text-xl lg:[&>*]:leading-relaxed [&_a]:underline [&_a]:transition-all [&_a]:duration-200 [&_a:hover]:no-underline"
                dangerouslySetInnerHTML={sanitizeHTML(data.closing_text.text)}
              />
            )}
            <Link
              href={data?.button?.href || ""}
              className="inline-block w-full lg:w-auto mt-8 lg:mt-16 px-9 py-3.5 bg-primary text-white rounded-full text-xl transition-all duration-300 hover:bg-[#122947] hover:shadow-lg text-center"
            >
              {data?.button?.label}
            </Link>
          </div>
          <div className="absolute lg:relative top-0 lg:top-auto w-full lg:w-2/5">
            <div className="relative h-80 lg:h-full w-full">
              {data?.image && (
                <Image
                  src={
                    data?.image?.image_file?.image?.id &&
                    data?.image?.image_file?.image?.filename_download
                      ? getReadableImageUrl(
                          data.image.image_file.image.id,
                          data.image.image_file.image.filename_download
                        )
                      : "/images/award.png"
                  }
                  alt={data?.image?.alt || ""}
                  fill
                  className="object-cover object-top"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
